import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useShipping } from '../../../hooks/useShipping';
import { useCurrency } from '../../../context/CurrencyContext';
import { useAuth } from '../../../context/AuthContext';
import usePrice from '../../../hooks/usePrice';
import { Parcel } from '../../../components/calculator/utils/calculationUtils';
import {
  ParcelDetails,
  CollectionDetailsData,
  ShippingService,
} from '../../../components/calculator/components';
import { ShippingRate } from '../../../services/shippingService';
import { UnitSystem } from '../../../components/calculator/UnitSystem';
import { ExtendedParcel, ExtendedDocument } from '../types';
import { fedexPackagingOptions, normalizeProviderCode, isFedexProvider } from '../constants/shippingOptions';
import {
  formatRates,
  calculateInsuranceCost,
  buildRatesRequest,
  buildPaymentData,
} from './shippingWizardHelpers';

export enum ShippingSteps {
  PACKAGE_DETAILS = 0,
  SERVICE_SELECTION = 1,
  PACKAGE_CONTENTS = 2,
  SUMMARY = 3,
}

export function useShippingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { convertPrice } = useCurrency();
  const { getShippingRates, isLoadingRates, ratesError, apiError } = useShipping();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState<ShippingSteps>(ShippingSteps.PACKAGE_DETAILS);
  const [completedSteps, setCompletedSteps] = useState<Set<ShippingSteps>>(new Set());
  const [stepValidation, setStepValidation] = useState<Record<ShippingSteps, boolean>>({
    [ShippingSteps.PACKAGE_DETAILS]: false,
    [ShippingSteps.SERVICE_SELECTION]: false,
    [ShippingSteps.PACKAGE_CONTENTS]: false,
    [ShippingSteps.SUMMARY]: false,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoadFailed, setInitialLoadFailed] = useState<boolean>(false);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [apiRates, setApiRates] = useState<ShippingRate[]>([]);
  const [shippingRates, setShippingRates] = useState<ShippingService[]>([]);
  const [locationState, setLocationState] = useState<any>(null);
  const [isUpdatingRates, setIsUpdatingRates] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [destinationPostalCodeError, setDestinationPostalCodeError] = useState<boolean>(false);

  const [unitSystem] = useState<UnitSystem>('metric');
  const [currentProvider, setCurrentProvider] = useState<string | null>(null);
  const [currentProviderCode, setCurrentProviderCode] = useState<string | null>(null);

  const [parcels, setParcels] = useState<ExtendedParcel[]>([]);
  const [parcelErrors, setParcelErrors] = useState<Record<string, Record<string, boolean>>>({});
  const [shouldRefreshRates, setShouldRefreshRates] = useState<boolean>(false);
  const [defaultShippingId, setDefaultShippingId] = useState<string>('');
  const [defaultShippingName, setDefaultShippingName] = useState<string>('');
  const [senderIdTouched, setSenderIdTouched] = useState<boolean>(false);
  const [senderNameTouched, setSenderNameTouched] = useState<boolean>(false);

  const [parcelDetails, setParcelDetails] = useState<ParcelDetails>({
    goodsValue: '', quantity: '', weightValue: '', weightUnit: 'kg',
    length: '', width: '', height: '', dimensionUnit: 'cm',
    packageType: 'package', itemsCount: '1', itemDescription: '',
    declaredValue: '', currency: 'ILS', insuranceRequired: false, insuranceCost: '20',
  });

  const [vatType, setVatType] = useState<string>('vat');
  const [collectionDetails, setCollectionDetails] = useState<CollectionDetailsData>({
    date: new Date(new Date().setDate(new Date().getDate() + 1)),
    timeFrom: new Date(new Date().setHours(9, 0, 0, 0)),
    timeTo: new Date(new Date().setHours(17, 0, 0, 0)),
    instructions: '',
  });
  const [signatureRequired, setSignatureRequired] = useState<boolean>(false);
  const [vatNumber, setVatNumber] = useState<string>('228837');
  const [documents, setDocuments] = useState<ExtendedDocument[]>([]);
  const [shipmentPurpose, setShipmentPurpose] = useState<string>('Commercial');
  const [shippingDescription, setShippingDescription] = useState<string>('');
  const [itemsValidation, setItemsValidation] = useState<Record<string, boolean>>({});
  const [newlyAddedItemId, setNewlyAddedItemId] = useState<string | null>(null);

  const isInitializedRef = useRef(false);
  const ratesRequestIdRef = useRef(0);

  const selectedService = shippingRates.find((rate) => rate.id === selectedRateId);

  // Price hooks
  const formattedInsuranceCost = usePrice({
    amount: convertPrice(parseFloat(parcelDetails.insuranceCost)),
    currencyCode: selectedService?.currency || parcelDetails.currency || 'ILS',
  });
  const formattedShippingCost = usePrice({
    amount: convertPrice(selectedService?.price || 0, selectedService?.currency || parcelDetails.currency || 'ILS'),
    currencyCode: selectedService?.currency || parcelDetails.currency || 'ILS',
  });
  const formattedTotalCost = usePrice({
    amount:
      (selectedService?.price || 0) +
      (parcelDetails.packageType === 'package' && parcelDetails.insuranceRequired
        ? convertPrice(parseFloat(parcelDetails.insuranceCost))
        : 0),
    currencyCode: selectedService?.currency || parcelDetails.currency || 'ILS',
  });
  const formattedSignatureCost = usePrice({
    amount: convertPrice(5.0),
    currencyCode: selectedService?.currency || parcelDetails.currency || 'ILS',
  });

  // Validation functions
  const validateParcelItems = (parcel: ExtendedParcel): boolean => {
    if (!parcel.items || parcel.items.length === 0) return false;
    const totalItemWeight = parcel.items.reduce((t, item) => t + item.weight * item.quantity, 0);
    const chargeableWeight = Math.max(
      typeof parcel.physicalWeight === 'number' ? parcel.physicalWeight : (parcel.weight || 0) * (parcel.quantity || 1),
      typeof parcel.dimensionalWeight === 'number' ? parcel.dimensionalWeight : 0
    );
    if (totalItemWeight > chargeableWeight) return false;
    return parcel.items.every(
      (item) => item.description && item.description.length >= 10 && item.quantity > 0 && item.weight > 0 && item.price >= 0
    );
  };

  const validateParcels = () => {
    const errors: Record<string, Record<string, boolean>> = {};
    let hasErrors = false;
    const isFedex = isFedexProvider(selectedService?.provider, selectedService?.providerCode);

    parcels.forEach((parcel) => {
      const parcelError: Record<string, boolean> = {};
      if (!parcel.quantity || parcel.quantity <= 0) { parcelError.quantity = true; hasErrors = true; }
      if (!parcel.weight || parcel.weight <= 0) {
        parcelError.weight = true; hasErrors = true;
      } else if (isFedex && parcel.fedexPackagingType && parcel.fedexPackagingType !== 'YOUR_PACKAGING') {
        const opt = fedexPackagingOptions.find((o) => o.value === parcel.fedexPackagingType);
        if (opt && parcel.weight > opt.maxWeight) { parcelError.weightLimit = true; parcelError.weight = true; hasErrors = true; }
      }
      if (parcelDetails.packageType === 'package') {
        if (!parcel.length || parcel.length <= 0) { parcelError.length = true; hasErrors = true; }
        if (!parcel.width || parcel.width <= 0) { parcelError.width = true; hasErrors = true; }
        if (!parcel.height || parcel.height <= 0) { parcelError.height = true; hasErrors = true; }
        if (parcel.showPrice && (!parcel.price || parcel.price < 0.01)) {
          if (!parcel.price || parcel.price <= 0) {
            const up = [...parcels]; up[parcels.indexOf(parcel)] = { ...parcel, price: 0.01 }; setParcels(up);
          } else { parcelError.price = true; hasErrors = true; }
        }
      }
      if (Object.keys(parcelError).length > 0) errors[parcel.id] = parcelError;
    });
    setParcelErrors(errors);
    return !hasErrors;
  };

  const validateDocuments = () => {
    if (documents.length === 0) return false;
    for (const doc of documents) {
      if (!doc.quantity || doc.quantity <= 0 || !doc.weight || doc.weight <= 0 || doc.weight > 0.5) return false;
    }
    return true;
  };

  const validatePackageDetails = (): boolean => {
    if (parcelDetails.packageType === 'package') return validateParcels();
    if (parcelDetails.packageType === 'document') return validateDocuments();
    return false;
  };

  const validateServiceSelection = (): boolean => selectedRateId !== null && shippingRates.length > 0;

  const validatePackageContents = (): boolean => {
    if (parcelDetails.packageType !== 'package') return true;
    let isValid = true;
    const validation: Record<string, boolean> = {};
    parcels.forEach((parcel) => {
      const ok = validateParcelItems(parcel);
      validation[parcel.id] = ok;
      if (!ok) isValid = false;
    });
    parcels.forEach((parcel) => {
      parcel.items?.forEach((item) => {
        if (!item.description || item.description.length < 10) {
          validation[`${parcel.id}_item_${item.id}`] = false;
          isValid = false;
        }
      });
    });
    if (!shipmentPurpose) isValid = false;
    setItemsValidation(validation);
    return isValid;
  };

  const validateSummary = (): boolean =>
    validatePackageDetails() &&
    validateServiceSelection() &&
    validatePackageContents() &&
    defaultShippingId.trim().length > 0 &&
    defaultShippingName.trim().length > 0;

  // Fetch shipping rates
  const fetchShippingRates = async (addressData: any) => {
    const requestId = ratesRequestIdRef.current + 1;
    ratesRequestIdRef.current = requestId;
    try {
      setIsUpdatingRates(true);
      setErrorMessage('');
      if (apiError) setSelectedRateId(null);
      const { requestData } = buildRatesRequest({
        addressData, parcels, parcelDetails, unitSystem,
        selectedRateId, shippingRates, currentProviderCode, currentProvider,
      });
      const rates = await getShippingRates(requestData);
      if (requestId !== ratesRequestIdRef.current) return;
      const nextApiRates = Array.isArray(rates?.services) ? rates.services : [];
      const formattedRates = formatRates(nextApiRates);
      setApiRates(nextApiRates);
      setShippingRates(formattedRates);
      if (formattedRates.length > 0) {
        const cachedSelectedId = location.state?.cachedShippingData?.selectedRateId;
        const stillExists = (id: string | null | undefined) =>
          !!id && formattedRates.some((r) => r.id === id);
        if (!stillExists(selectedRateId)) {
          if (stillExists(cachedSelectedId)) {
            setSelectedRateId(cachedSelectedId);
          } else {
            setSelectedRateId(formattedRates[0].id);
          }
        }
        setDestinationPostalCodeError(false);
      } else {
        setErrorMessage('No shipping rates available for the selected addresses.');
        setDestinationPostalCodeError(true);
      }
      const cachedSelectedRateId = location.state?.cachedShippingData?.selectedRateId;
      if (cachedSelectedRateId) {
        const stillAvailable = formattedRates.find((r) => r.id === cachedSelectedRateId);
        if (stillAvailable) {
          setCurrentStep(ShippingSteps.PACKAGE_CONTENTS);
          setCompletedSteps((prev) => {
            const s = new Set(prev);
            s.add(ShippingSteps.PACKAGE_DETAILS);
            s.add(ShippingSteps.SERVICE_SELECTION);
            return s;
          });
          const old = location.state.cachedShippingData.rates?.find((r: any) => r.id === cachedSelectedRateId);
          if (old && Math.abs(stillAvailable.price - old.price) > 0.01) {
            setErrorMessage(`Note: the rate price was updated from ${old.price} to ${stillAvailable.price}.`);
          }
        }
      }
      setShouldRefreshRates(false);
    } catch {
      if (requestId !== ratesRequestIdRef.current) return;
      setErrorMessage('Failed to fetch shipping rates. Please try again.');
      setDestinationPostalCodeError(true);
    } finally {
      if (requestId === ratesRequestIdRef.current) setIsUpdatingRates(false);
    }
  };

  // User effect
  useEffect(() => {
    if (user) {
      const idValue = user.default_shipping_id != null ? String(user.default_shipping_id) : '';
      const nameValue = user.default_shipping_name ?? '';
      setDefaultShippingId(idValue);
      setDefaultShippingName(nameValue);
      if (idValue.trim() && nameValue.trim()) {
        setSenderIdTouched(false);
        setSenderNameTouched(false);
        setErrorMessage((prev) => prev === 'Sender ID number and name are required' ? '' : prev);
      }
    }
  }, [user]);

  // Init effect
  useEffect(() => {
    if (isInitializedRef.current) return;
    if (!location.state?.selectedSourceAddress || !location.state?.selectedDestinationAddress || !location.state?.parcels) {
      navigate('/shipping'); return;
    }
    setLoading(true);
    setParcels(location.state.parcels.map((p: Parcel) => ({ ...p, showPrice: true, fedexPackagingType: 'YOUR_PACKAGING' })));
    setDocuments([{ id: `document-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, quantity: 1, weight: 0.5, size: 'large_envelope' }]);
    if (location.state.shippingType) setParcelDetails((prev) => ({ ...prev, packageType: location.state.shippingType }));
    if (location.state.origin && typeof location.state.origin === 'object') location.state.origin = location.state.origin.code;
    if (location.state.destination && typeof location.state.destination === 'object') location.state.destination = location.state.destination.code;
    setLocationState(location.state);
    if (location.state.selectedRateProvider) setCurrentProvider(location.state.selectedRateProvider);
    if (location.state.selectedRateProviderCode) setCurrentProviderCode(location.state.selectedRateProviderCode);
    else if (location.state.selectedRateProvider) setCurrentProviderCode(normalizeProviderCode(location.state.selectedRateProvider));
    const cachedSelectedRateId = location.state?.cachedShippingData?.selectedRateId;
    if (cachedSelectedRateId) {
      setSelectedRateId(cachedSelectedRateId);
    }
    (async () => {
      try { await fetchShippingRates(location.state); } catch { if (loading) setInitialLoadFailed(true); } finally { setLoading(false); }
    })();
    isInitializedRef.current = true;
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step validation effect
  useEffect(() => {
    setStepValidation({
      [ShippingSteps.PACKAGE_DETAILS]: validatePackageDetails(),
      [ShippingSteps.SERVICE_SELECTION]: validateServiceSelection(),
      [ShippingSteps.PACKAGE_CONTENTS]: validatePackageContents(),
      [ShippingSteps.SUMMARY]: validateSummary(),
    });
  }, [parcels, documents, parcelDetails, selectedRateId, shippingRates, shipmentPurpose, defaultShippingId, defaultShippingName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Insurance effect
  useEffect(() => {
    const goodsTotal = parseFloat(parcelDetails.goodsValue) * parseInt(parcelDetails.quantity || '1', 10);
    let totalValue = isNaN(goodsTotal) ? '0' : goodsTotal.toString();
    const parcelsTotalValue = parcels.reduce((t, p) => t + (p.items?.reduce((pt, item) => pt + item.price * item.quantity, 0) || 0), 0).toString();
    if (parseFloat(parcelsTotalValue) > 0) totalValue = parcelsTotalValue;
    setParcelDetails((prev) => ({ ...prev, declaredValue: totalValue, insuranceCost: calculateInsuranceCost(totalValue) }));
  }, [parcels, parcelDetails.goodsValue, parcelDetails.quantity]); // eslint-disable-line react-hooks/exhaustive-deps

  // API error effect
  useEffect(() => {
    if (ratesError) {
      setErrorMessage('Failed to fetch shipping rates. Please check the address information.');
      setDestinationPostalCodeError(true);
      if (loading) { setInitialLoadFailed(true); setTimeout(() => handleBackToSearch(), 2000); return; }
      if (selectedRateId) {
        const sel = shippingRates.find((r) => r.id === selectedRateId);
        if (sel) { setCurrentProvider(sel.provider); setCurrentProviderCode(sel.providerCode || normalizeProviderCode(sel.provider)); }
      }
    } else if (apiError) {
      setErrorMessage(apiError.error_message || `Failed to get rates from ${apiError.provider || 'provider'}.`);
      setDestinationPostalCodeError(false);
      if (loading) { setInitialLoadFailed(true); setTimeout(() => handleBackToSearch(), 2000); return; }
      if (apiError.provider) {
        const norm = normalizeProviderCode(apiError.provider);
        const match = shippingRates.find((r) => normalizeProviderCode(r.providerCode || r.provider) === norm);
        setCurrentProvider(match?.provider || apiError.provider || null);
        setCurrentProviderCode(apiError.provider);
      }
    }
  }, [ratesError, apiError, selectedRateId, shippingRates, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset refresh flag
  useEffect(() => {
    if (!isLoadingRates && apiRates && apiRates.length > 0) setShouldRefreshRates(false);
  }, [isLoadingRates, apiRates]); // eslint-disable-line react-hooks/exhaustive-deps

  const researchShippingRates = () => {
    setErrorMessage('');
    setDestinationPostalCodeError(false);
    if (parcelDetails.packageType === 'package') {
      if (!validateParcels()) { setErrorMessage('Please fill in all required parcel details'); return; }
    } else {
      if (!validateDocuments()) { setErrorMessage('Please fill in all required document details'); return; }
    }
    if (!locationState) { setErrorMessage('Cannot research rates: missing location data'); return; }
    setIsUpdatingRates(true);
    setErrorMessage('');
    const selSvc = selectedRateId ? shippingRates.find((r) => r.id === selectedRateId) : null;
    const selProvider = selSvc?.provider || locationState.selectedRateProvider;
    const selProviderCode = selSvc?.providerCode || locationState.selectedRateProviderCode || normalizeProviderCode(selProvider);
    fetchShippingRates({
      ...locationState,
      shippingType: parcelDetails.packageType,
      parcels: parcelDetails.packageType === 'package'
        ? parcels.map((p) => ({ id: p.id, quantity: p.quantity, weight: p.weight, length: p.length, width: p.width, height: p.height, physicalWeight: p.physicalWeight || p.weight, dimensionalWeight: p.dimensionalWeight || 0, items: p.items || [], fedexPackagingType: p.fedexPackagingType || 'YOUR_PACKAGING' }))
        : [],
      documents: parcelDetails.packageType === 'document'
        ? documents.map((d) => ({ id: d.id, quantity: d.quantity, weight: d.weight, size: d.size }))
        : [],
      unitSystem,
      selectedRateProvider: selProvider,
      selectedRateProviderCode: selProviderCode,
    });
    setShouldRefreshRates(false);
  };

  const goToNextStep = () => {
    const next = currentStep + 1;
    if (next <= ShippingSteps.SUMMARY) {
      setCompletedSteps((prev) => { const s = new Set(prev); s.add(currentStep); return s; });
      setCurrentStep(next);
      if (next === ShippingSteps.SERVICE_SELECTION) { researchShippingRates(); setShouldRefreshRates(false); }
      window.scrollTo(0, 0);
    }
  };

  const goToStep = (step: ShippingSteps) => {
    if (step === currentStep) { setCurrentStep(step); window.scrollTo(0, 0); }
  };

  const handleBackToSearch = () => {
    if (location.state?.cachedShippingData) {
      navigate('/shipping', { state: { fromShippingOrder: true, cachedData: location.state.cachedShippingData } });
    } else {
      navigate('/shipping');
    }
  };

  const handleProceedToPayment = () => {
    setErrorMessage('');
    if (apiError || ratesError) { setErrorMessage('Please resolve the error and select a shipping rate to proceed.'); return; }
    if (!locationState?.selectedSourceAddress || !locationState?.selectedDestinationAddress) { setErrorMessage('Sender and recipient addresses are required'); return; }
    if (!selectedRateId) { setErrorMessage('Please select a shipping rate to proceed.'); return; }
    if (shouldRefreshRates) { setErrorMessage('Parcel details have changed. Please refresh rates before proceeding.'); return; }
    if (parcelDetails.packageType === 'package' && !validateParcels()) { setErrorMessage('Please fill in all required parcel details'); return; }
    if (parcelDetails.packageType === 'document' && !validateDocuments()) { setErrorMessage('Please fill in all required document details'); return; }
    if (!defaultShippingId.trim() || !defaultShippingName.trim()) { setSenderIdTouched(true); setSenderNameTouched(true); setErrorMessage('Sender ID number and name are required'); return; }
    const selRate = shippingRates.find((r) => r.id === selectedRateId);
    if (!selRate) { setErrorMessage('Selected rate not found. Please select a rate again.'); return; }
    setIsUpdatingRates(true);
    const paymentData = buildPaymentData({
      locationState, parcels, documents, packageType: parcelDetails.packageType,
      collectionDetails, defaultShippingId, defaultShippingName, selectedRate: selRate,
      insuranceRequired: parcelDetails.insuranceRequired, declaredValue: parcelDetails.declaredValue,
      signatureRequired, shipmentPurpose, vatType, vatNumber, shippingDescription,
    });
    setTimeout(() => { setIsUpdatingRates(false); navigate('/shipping-payment', { state: paymentData }); }, 300);
  };

  return {
    currentStep, completedSteps, stepValidation, loading, initialLoadFailed,
    selectedRateId, setSelectedRateId, apiRates, shippingRates, isUpdatingRates, isLoadingRates,
    ratesError, apiError, shouldRefreshRates, setShouldRefreshRates,
    errorMessage, setErrorMessage, destinationPostalCodeError, setDestinationPostalCodeError,
    locationState, setLocationState,
    parcels, setParcels, parcelErrors, setParcelErrors, parcelDetails, setParcelDetails, unitSystem,
    documents, setDocuments,
    currentProvider, setCurrentProvider, currentProviderCode, setCurrentProviderCode,
    vatType, setVatType, vatNumber, setVatNumber,
    collectionDetails, setCollectionDetails,
    signatureRequired, setSignatureRequired,
    shipmentPurpose, setShipmentPurpose, shippingDescription, setShippingDescription, itemsValidation,
    newlyAddedItemId, setNewlyAddedItemId,
    defaultShippingId, setDefaultShippingId, defaultShippingName, setDefaultShippingName,
    senderIdTouched, setSenderIdTouched, senderNameTouched, setSenderNameTouched,
    selectedService, formattedShippingCost, formattedInsuranceCost, formattedTotalCost, formattedSignatureCost,
    calculateInsuranceCost,
    fetchShippingRates, researchShippingRates, goToNextStep, goToStep,
    handleBackToSearch, handleProceedToPayment,
    validateParcelItems, validateParcels, validateDocuments, validatePackageContents,
  };
}
